import React, { FC, useState, useEffect } from 'react'
import { Link } from 'gatsby-plugin-react-i18next'
import { useLocation } from '@reach/router'
import { motion, AnimatePresence } from 'framer-motion'
import logoDark from 'src/images/svg/logo-full-dark.svg'
import logoLight from 'src/images/svg/logo-full-light.svg'
import ButtonIcon, { ButtonSizes } from 'src/components/ui/ButtonIcon'
import ThemeToggle from 'src/components/ui/ThemeToggle'
import AnniversaryTicker from 'src/components/ui/AnniversaryTicker'
import HeaderSearch from 'src/components/layout/HeaderSearch'
import { IMobileHeaderProps } from './types'

export interface IMobileHeaderComponentProps extends IMobileHeaderProps {
  isMenuOpen: boolean
  setIsMenuOpen: (open: boolean) => void
  isCollapsed: boolean
}

export const MobileHeader: FC<IMobileHeaderComponentProps> = ({
  menuItems,
  showAnniversaryTicker = true,
  showSearch = false,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  isMenuOpen,
  setIsMenuOpen,
  isCollapsed,
  isHome: propIsHome,
}) => {
  const location = useLocation()
  const browserPath = typeof window !== 'undefined' ? window.location.pathname : ''
  const currentPathname = browserPath || location?.pathname || ''
  const rawPath = currentPathname.replace(/^\/(?:pl|en)(?=\/|$)/, '') || '/'
  const cleanPath = rawPath.replace(/\/$/, '') || '/'
  const isHome = propIsHome ?? (cleanPath === '/')

  const [isVisible, setIsVisible] = useState(true)
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    let contactSection = document.getElementById('contact-footer')
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting)
        if (entry.isIntersecting) {
          setIsMenuOpen(false)
        }
      },
      {
        rootMargin: '0px 0px -5% 0px',
        threshold: 0,
      }
    )

    if (contactSection) {
      observer.observe(contactSection)
    } else {
      const timer = setTimeout(() => {
        contactSection = document.getElementById('contact-footer')
        if (contactSection) {
          observer.observe(contactSection)
        }
      }, 300)
      return () => {
        clearTimeout(timer)
        if (contactSection) observer.unobserve(contactSection)
      }
    }

    return () => {
      if (contactSection) observer.unobserve(contactSection)
    }
  }, [location.pathname, setIsMenuOpen])

  const initialAnimation = !hasMounted ? false : { opacity: 0, y: 60 }

  return (
    <>
      {showAnniversaryTicker && (
        <div className="md:hidden relative z-40">
          <AnniversaryTicker />
        </div>
      )}

      {/* Mobile Bar Dropdown (Fixed at bottom on mobile) */}
      <div
        className="md:hidden fixed left-4 right-4 sm:left-8 sm:right-8 z-50 flex justify-center pointer-events-none"
        style={{
          left: 'var(--grid-margin, 16px)',
          right: 'var(--grid-margin, 16px)',
          bottom: 'calc(var(--grid-margin, 16px) + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <AnimatePresence>
          {isVisible && (
            <motion.div
              key="mobile-bottom-bar"
              initial={initialAnimation}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 60 }}
              transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
              className="w-full max-w-full bg-highlight-base dark:bg-surface/60 backdrop-blur-2xl border border-outline-lower shadow-elevation-highest overflow-hidden pointer-events-auto flex flex-col-reverse justify-start relative rounded-container-semi-large"
            >
          {/* Bottom Bar Row */}
          <div className="relative w-full h-extra-huge flex items-center justify-between min-w-extra-huge shrink-0">
            {/* Logo Container */}
            <div className="pl-medium flex items-center whitespace-nowrap">
              <Link to="/" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
                <img src={logoDark} alt="MAMF logo" className="w-20 max-w-none dark:hidden" />
                <img src={logoLight} alt="MAMF logo" className="w-20 max-w-none hidden dark:block" />
              </Link>
            </div>

            {/* Right Actions (Theme Toggle + Divider + Hamburger) */}
            <div className="flex items-center gap-pico pr-pico">
              <ThemeToggle
                minimal
                textColor="text-on-surface"
                className="opacity-100 p-2 min-w-wcag-min-target min-h-wcag-min-target"
                iconClassName="w-5 h-5"
              />
              <div className="w-constant-1 h-semi-large bg-outline-lower opacity-50 shrink-0" />
              <ButtonIcon
                type="button"
                isOutline
                onClick={(e) => {
                  e?.stopPropagation()
                  setIsMenuOpen(!isMenuOpen)
                }}
                morphingIcon="hamburger"
                isOpen={isMenuOpen}
                size={ButtonSizes.large}
                className="!bg-transparent text-on-surface border-0 shadow-none focus:outline-none opacity-100"
                title={isMenuOpen ? 'Close menu' : 'Open menu'}
              />
            </div>
          </div>

          <AnimatePresence>
            {isMenuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden w-full max-h-[calc(100dvh-120px)] overflow-y-auto no-scrollbar"
              >
                {/* Fixed-width wrapper to prevent text wrapping layout jumps */}
                <div className="w-full">
                  {showSearch && (
                    <div className="px-semi-large pt-base pb-extra-small">
                      <HeaderSearch
                        value={searchValue}
                        onChange={onSearchChange}
                        placeholder={searchPlaceholder}
                        variant="mobile"
                      />
                    </div>
                  )}
                  <ul className="flex flex-col gap-base px-semi-large pt-semi-large pb-small">
                    {menuItems.map((item) => {
                      const isActive = item.partiallyActive
                        ? cleanPath.startsWith(item.url.replace(/\/$/, ''))
                        : cleanPath === item.url.replace(/\/$/, '')

                      return (
                        <li key={item.name}>
                          <Link
                            to={item.url}
                            partiallyActive={item.partiallyActive}
                            onClick={() => setIsMenuOpen(false)}
                            className={`text-on-surface text-title-medium font-palanquin font-bold lowercase flex items-center min-h-wcag-min-target py-pico transition-opacity duration-300 hover:opacity-100 ${
                              isActive ? 'opacity-100' : 'opacity-70'
                            }`}
                          >
                            {item.name}
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
      </div>
    </>
  )
}

export default MobileHeader
